import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  collection,
  getDocs,
  query,
  orderBy,
} from "firebase/firestore";
import { db } from "../firebase";
import "./Trainers.css";

function Trainers() {
  const { t, i18n } = useTranslation();

  const [trainers, setTrainers] = useState([]);
  const [loading, setLoading] = useState(true);

  const currentLanguage =
    i18n.resolvedLanguage === "en" ||
    i18n.language?.startsWith("en")
      ? "en"
      : "ar";

  useEffect(() => {
    const fetchTrainers = async () => {
      try {
        const trainersRef = collection(db, "trainers");

        const q = query(
          trainersRef,
          orderBy("createdAt", "desc")
        );

        const snapshot = await getDocs(q);

        const data = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        setTrainers(data);
      } catch (error) {
        console.error("Error loading trainers:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchTrainers();
  }, []);

  const getValue = (trainer, field) => {
    if (currentLanguage === "en") {
      return trainer[`${field}En`] || trainer[`${field}Ar`] || "";
    }

    return trainer[`${field}Ar`] || trainer[`${field}En`] || "";
  };

  return (
    <main className="trainers-page">

      {/* ================= HERO ================= */}

      <section className="trainers-hero">

        <div className="trainers-hero-content">

          <span className="trainers-tag">
            {t("trainersPage.tag")}
          </span>

          <h1>
            {t("trainersPage.title")}
            <span>{t("trainersPage.titleHighlight")}</span>
          </h1>

          <p>
            {t("trainersPage.description")}
          </p>

        </div>

        <div className="hero-shape blue-shape"></div>
        <div className="hero-shape red-shape"></div>
        <div className="hero-shape yellow-shape"></div>

      </section>

      {/* ================= TRAINERS ================= */}

      <section className="trainers-section">

        <div className="section-heading">

          <span className="small-title">
            {t("trainersPage.teamTag")}
          </span>

          <h2>
            {t("trainersPage.teamTitle")}
          </h2>

          <p>
            {t("trainersPage.teamText")}
          </p>

        </div>

        {/* LOADING */}

        {loading && (
          <div className="trainers-loading">
            <div className="loader"></div>
            <p>{t("trainersPage.loading")}</p>
          </div>
        )}

        {/* EMPTY */}

        {!loading && trainers.length === 0 && (
          <div className="empty-trainers">
            <div className="empty-icon">👨‍🏫</div>

            <h3>
              {t("trainersPage.emptyTitle")}
            </h3>

            <p>
              {t("trainersPage.emptyText")}
            </p>
          </div>
        )}

        {/* CARDS */}

        {!loading && trainers.length > 0 && (
          <div className="trainers-grid">

            {trainers.map((trainer, index) => {

              const colorClass =
                index % 3 === 0
                  ? "trainer-blue"
                  : index % 3 === 1
                  ? "trainer-red"
                  : "trainer-yellow";

              return (
                <article
                  className={`trainer-card ${colorClass}`}
                  key={trainer.id}
                >

                  {/* IMAGE */}

                  <div className="trainer-image-wrapper">

                    {trainer.image ? (
                      <img
                        src={trainer.image}
                        alt={getValue(trainer, "name")}
                        className="trainer-image"
                      />
                    ) : (
                      <div className="trainer-no-image">
                        <span>👨‍🏫</span>
                      </div>
                    )}

                    <div className="trainer-number">
                      {String(index + 1).padStart(2, "0")}
                    </div>

                  </div>

                  {/* CONTENT */}

                  <div className="trainer-content">

                    <h3>
                      {getValue(trainer, "name")}
                    </h3>

                    <div className="trainer-specialty">
                      {getValue(trainer, "specialty")}
                    </div>

                    {trainer.experience && (
                      <div className="trainer-experience">

                        <span className="experience-icon">
                          ✦
                        </span>

                        <span>
                          {trainer.experience}{" "}
                          {t("trainersPage.experience")}
                        </span>

                      </div>
                    )}

                    {getValue(trainer, "bio") && (
                      <p className="trainer-bio">
                        {getValue(trainer, "bio")}
                      </p>
                    )}

                  </div>

                </article>
              );
            })}

          </div>
        )}

      </section>

      {/* ================= CTA ================= */}

      <section className="trainers-cta">

        <div className="cta-content">

          <span>
            {t("trainersPage.ctaTag")}
          </span>

          <h2>
            {t("trainersPage.ctaTitle")}
          </h2>

          <p>
            {t("trainersPage.ctaText")}
          </p>

          <Link
            to="/registration"
            className="cta-button"
          >
            {t("trainersPage.ctaButton")}
          </Link>

        </div>

      </section>

    </main>
  );
}

export default Trainers;